import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Main, MainContent, Section } from '@prosolve/common-components-ui'
import useIsMobileView from '../hooks/useIsMobileView';
import useSnackbar from '../hooks/useSnackbar';
import useDataAssets from '../dataHooks/useDataAssets';
import { getParentPath } from './assetNameUtils';
import { downloadAssetFolder } from './downloadUtils';
import { assetTheme as t } from './assetTheme';
import AssetManagerTopNav from './AssetManagerTopNav';
import GoHubTopNav from '../sharedComponents/GoHubTopNav';
import Breadcrumbs from './Breadcrumbs';
import AssetToolbar from './AssetToolbar';
import AssetGrid from './AssetGrid';
import AssetListView from './AssetListView';
import ViewToggle from './ViewToggle';
import FilePreviewModal from './FilePreviewModal';
import UploadFilesModal from './UploadFilesModal';
import NewFolderModal from './NewFolderModal';
import RenameModal from './RenameModal';
import UrlChangeConfirmationModal from './UrlChangeConfirmationModal';
import NoticeModal from './NoticeModal';
import PillButton from './PillButton';

const VIEW_MODE_STORAGE_KEY = 'assetManagerViewMode';

// Root folders other ProSolve apps read by name: the teacher portal's lesson images and its Resources page.
// Deleting or renaming them would break those apps, so the UI refuses (the API itself still allows it).
const RESERVED_FOLDER_PATHS = ['HubLessonImages', 'HubResources'];
const isReservedFolder = (folder) => RESERVED_FOLDER_PATHS.includes(folder.path);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 18px;
  padding: ${({ $isSelectMode }) => $isSelectMode ? '10px 15px 90px' : '10px 15px'};
  font-family: ${t.fontFamily};
  background: ${t.bg};
  min-height: 100%;
`

const SelectBar = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
`

const FloatingSelectButton = styled(PillButton)`
  padding: 12px 34px;
  box-shadow: 0 2px 8px rgba(0,19,35,.18);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: ${t.midnight};
    }
  }
`

const CrumbRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export default ({ isPrivate }) => {
  const isMobileView = useIsMobileView();
  const snackbar = useSnackbar();

  // Embed-mode params (TECH-961). loadFolder deep-links to a folder, falling back
  // to root if it has no contents ('manage' is reserved by the API routes, so it
  // never resolves). hasSelectAndCloseControls swaps click-to-preview for
  // click-to-select and shows a floating Select button that posts the chosen file
  // to the embedding window.
  const searchParams = new URLSearchParams(window.location.search);
  const requestedFolder = (searchParams.get('loadFolder') || '').replace(/^\/+|\/+$/g, '');
  const isSelectMode = searchParams.get('hasSelectAndCloseControls') === 'true';
  const {
    fetchAssetList,
    uploadAssetFiles,
    createFolder,
    deleteFolder,
    renameFolder,
    deleteFile,
    moveFile,
    renameFile
  } = useDataAssets();

  // null = still resolving the loadFolder deep link; '' = root
  const [currentPath, setCurrentPath] = useState(requestedFolder ? null : '')
  const [folders, setFolders] = useState([])
  const [files, setFiles] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isTruncated, setIsTruncated] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [previewFile, setPreviewFile] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [modalState, setModalState] = useState(null)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'list' ? 'list' : 'grid';
  })

  const closeModal = () => setModalState(null)

  const onViewModeChange = (mode) => {
    setViewMode(mode)
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  }

  const loadFolder = async (prefix = currentPath) => {
    try {
      setIsLoading(true)
      const result = await fetchAssetList(prefix)
      setFolders(result.folders || [])
      setFiles(result.files || [])
      setIsTruncated(!!result.isTruncated)
    } catch (e) {
      console.log(e)
      snackbar.newMessage({ message: 'Unable to load assets', severity: 'failure' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentPath === null) return;
    setSearchTerm('')
    setSelectedFile(null)
    loadFolder(currentPath)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  // Resolve the loadFolder deep link once at mount: navigate there if the folder
  // appears in its parent's listing (which covers empty folders, since the
  // New Folder placeholder object surfaces them there), otherwise fall back to root.
  useEffect(() => {
    if (currentPath !== null) return;
    const resolveRequestedFolder = async () => {
      let targetPath = ''
      try {
        const result = await fetchAssetList(getParentPath(requestedFolder))
        if ((result.folders || []).some((folder) => folder.path === requestedFolder)) {
          targetPath = requestedFolder
        }
      } catch (e) {
        console.log(e)
      }
      setCurrentPath(targetPath)
    }
    resolveRequestedFolder()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const search = searchTerm.trim().toLowerCase();
  const visibleFolders = search ? folders.filter(({ name }) => name.toLowerCase().indexOf(search) > -1) : folders;
  const visibleFiles = search ? files.filter(({ name }) => name.toLowerCase().indexOf(search) > -1) : files;

  const runAction = async (actionFn, successMessage) => {
    try {
      const result = await actionFn()
      if (successMessage) {
        snackbar.newMessage({ message: successMessage, severity: 'success' })
      }
      await loadFolder(currentPath)
      return result;
    } catch (e) {
      console.log(e)
      const message = typeof e === 'string' ? e : 'Something went wrong'
      snackbar.newMessage({ message, severity: 'failure' })
    }
  }

  const requestConfirmedAction = ({ title, message, warningText, confirmLabel, onConfirm }) => {
    setModalState({
      type: 'confirm',
      props: {
        title,
        message,
        warningText,
        confirmLabel,
        onConfirm: () => {
          closeModal()
          onConfirm()
        }
      }
    })
  }

  const onDeleteFile = (file) => {
    requestConfirmedAction({
      title: 'Delete File',
      message: `Delete "${file.name}"?`,
      warningText: 'This permanently removes the file and its public URL. Any existing links, embeds, or documents pointing to this URL will stop working.',
      confirmLabel: 'Delete',
      onConfirm: () => runAction(() => deleteFile(file.key), `Deleted ${file.name}`)
    })
  }

  const onRenameFolder = (folder) => {
    if (isReservedFolder(folder)) {
      setModalState({
        type: 'notice',
        props: {
          title: 'Rename Folder',
          message: 'This folder is reserved and cannot be renamed.'
        }
      })
      return
    }
    setModalState({ type: 'rename', itemType: 'folder', item: folder })
  }

  const onDeleteFolder = (folder) => {
    if (isReservedFolder(folder)) {
      setModalState({
        type: 'notice',
        props: {
          title: 'Delete Folder',
          message: 'This folder is reserved and cannot be deleted.'
        }
      })
      return
    }
    requestConfirmedAction({
      title: 'Delete Folder',
      message: `Delete the folder "${folder.name}" and everything inside it?`,
      warningText: 'This permanently deletes all files in this folder (including subfolders) and their public URLs. Any existing links pointing to those URLs will stop working.',
      confirmLabel: 'Delete',
      onConfirm: () => runAction(() => deleteFolder(folder.path), `Deleted folder ${folder.name}`)
    })
  }

  const doMoveFile = async (sourceKey, destinationFolder, overwrite) => {
    const result = await runAction(() => moveFile(sourceKey, destinationFolder, overwrite))
    if (result && result.success === false && result.exists) {
      const filename = sourceKey.split('/').pop();
      requestConfirmedAction({
        title: 'File Already Exists',
        message: `A file named "${filename}" already exists in the destination folder.`,
        warningText: 'Replacing it keeps the destination URL but changes the content behind it, and the original file at the old URL will be gone.',
        confirmLabel: 'Replace',
        onConfirm: () => doMoveFile(sourceKey, destinationFolder, true)
      })
    } else if (result && result.success) {
      snackbar.newMessage({ message: 'File moved', severity: 'success' })
    }
  }

  const onDropFile = (e, destinationFolder) => {
    const payload = e.dataTransfer.getData('text/plain') || '';
    if (payload.indexOf('assetfile:') !== 0) return;

    const sourceKey = payload.slice('assetfile:'.length);
    if (getParentPath(sourceKey) === destinationFolder) return;

    const filename = sourceKey.split('/').pop();
    requestConfirmedAction({
      title: 'Move File',
      message: `Move "${filename}" to ${destinationFolder ? `"${destinationFolder}"` : 'the top level'}?`,
      onConfirm: () => doMoveFile(sourceKey, destinationFolder, false)
    })
  }

  const doRenameFile = async (file, newName, overwrite) => {
    const result = await runAction(() => renameFile(file.key, newName, overwrite))
    if (result && result.success === false && result.exists) {
      requestConfirmedAction({
        title: 'File Already Exists',
        message: `A file named "${newName}" already exists in this folder.`,
        warningText: 'Replacing it keeps that URL but changes the content behind it, and the original file will be gone.',
        confirmLabel: 'Replace',
        onConfirm: () => doRenameFile(file, newName, true)
      })
    } else if (result && result.success) {
      snackbar.newMessage({ message: `Renamed to ${newName}`, severity: 'success' })
    }
  }

  const onRenameSubmit = (itemType, item, newName) => {
    requestConfirmedAction({
      title: `Rename ${itemType === 'folder' ? 'Folder' : 'File'}`,
      message: `Rename "${item.name}" to "${newName}"?`,
      warningText: itemType === 'folder'
        ? 'This changes the public URL of every file inside this folder. Any existing links pointing to the old URLs will stop working.'
        : 'This changes the asset\'s public URL. Any existing links, embeds, or documents pointing to the old URL will stop working.',
      onConfirm: () => {
        if (itemType === 'folder') {
          runAction(() => renameFolder(item.path, newName), `Renamed folder to ${newName}`)
        } else {
          doRenameFile(item, newName, false)
        }
      }
    })
  }

  const renderModal = () => {
    if (!modalState) return null;

    if (modalState.type === 'upload') {
      return (
        <UploadFilesModal
          currentPath={currentPath}
          uploadAssetFiles={uploadAssetFiles}
          onClose={closeModal}
          onUploaded={() => {
            snackbar.newMessage({ message: 'Files uploaded', severity: 'success' })
            loadFolder(currentPath)
          }}
        />
      )
    }

    if (modalState.type === 'newFolder') {
      return (
        <NewFolderModal
          currentPath={currentPath}
          onClose={closeModal}
          onCreate={async (path) => {
            await runAction(() => createFolder(path), 'Folder created')
          }}
        />
      )
    }

    if (modalState.type === 'rename') {
      const { itemType, item } = modalState;
      return (
        <RenameModal
          itemType={itemType}
          currentName={item.name}
          currentKey={itemType === 'folder' ? item.path : item.key}
          onClose={closeModal}
          onSubmit={(newName) => onRenameSubmit(itemType, item, newName)}
        />
      )
    }

    if (modalState.type === 'confirm') {
      return <UrlChangeConfirmationModal {...modalState.props} onClose={closeModal} />
    }

    if (modalState.type === 'notice') {
      return <NoticeModal {...modalState.props} onClose={closeModal} />
    }

    return null;
  }

  // Sends the selected file to the embedding app (e.g. the teacher portal's
  // SelectImageButton), which closes its iframe modal on receipt. Sent to both
  // parent and opener, matching the SURVEY_COMPLETE convention.
  const onConfirmSelection = () => {
    if (!selectedFile) return;
    const payload = {
      type: 'ASSET_SELECTED',
      url: selectedFile.url,
      name: selectedFile.name,
      key: selectedFile.key,
      size: selectedFile.size
    }
    window.parent.postMessage(payload, '*')
    window.opener?.postMessage(payload, '*')
  }

  const listProps = {
    isLoading,
    folders: visibleFolders,
    files: visibleFiles,
    isMobileView,
    isSelectMode,
    selectedFile,
    onSelectFile: (file) => setSelectedFile((prev) => prev && prev.key === file.key ? null : file),
    onOpenFolder: setCurrentPath,
    onRenameFolder,
    onDeleteFolder,
    onDropFile,
    onPreviewFile: setPreviewFile,
    onRenameFile: (file) => setModalState({ type: 'rename', itemType: 'file', item: file }),
    onDeleteFile
  }

  return (
    <>
      { isPrivate && !isSelectMode && <GoHubTopNav appLogo={<AssetManagerTopNav />} /> }
      <Main $isMobileView={isMobileView} $backgroundColor={t.bg} $hasNavHeader={isPrivate && !isSelectMode} style={{ padding: '0', marginTop: (isPrivate && !isSelectMode) ? '39px' : undefined }}>
        <MainContent $isMobileView={isMobileView} style={{ width: '100%', maxWidth: '100%' }}>
          <Section style={{ padding: '0px', background: t.bg }}>
            <Wrapper $isSelectMode={isSelectMode}>
              <AssetToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isTruncated={isTruncated}
                canDownloadAll={!isLoading && (files.length > 0 || folders.length > 0)}
                onDownloadAll={() => downloadAssetFolder(currentPath)}
                onNewFolder={() => setModalState({ type: 'newFolder' })}
                onUploadFiles={() => setModalState({ type: 'upload' })}
              />
              <CrumbRow>
                <Breadcrumbs
                  currentPath={currentPath}
                  onNavigate={setCurrentPath}
                  onDropFile={onDropFile}
                />
                <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
              </CrumbRow>
              {
                viewMode === 'list' ?
                <AssetListView {...listProps} />
                :
                <AssetGrid {...listProps} />
              }
            </Wrapper>
          </Section>
        </MainContent>
      </Main>
      {renderModal()}
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
      {
        isSelectMode &&
        <SelectBar>
          <FloatingSelectButton $primary={true} disabled={!selectedFile} onClick={onConfirmSelection}>
            {'Select'}
          </FloatingSelectButton>
        </SelectBar>
      }
    </>
  )
}
